import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Carousel, { CarouselItem } from 'tbk-components/src/components/Carousel';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import ResourceCard, { IResourceCardProps } from '../ResourceCard';
import AnimatedArcs from '../AnimatedArcs';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Icon from 'tbk-components/src/components/Icon';

export interface IResourceCardsi18n {
	compareLabel: string;
	compareButtonLabel: string;
	cardCompareLabel: string;
	hideComparisonLabel: string;
	viewComparisonLabel: string;
}
export interface IResourceCardListingProps extends IBaseElementProps {
	title?: string;
	headingSize?: 'Small' | 'Large';
	/**
	 * Heading style 'Card' will only appear if listing style is in 'Grid'
	 */
	headingStyle?: 'Columned' | 'Centered' | 'Card';
	excerpt?: string;
	cta?: ILink;
	/**
	 * Enable this to get the latest resources  If this is enabled, ensure there are no resources entered below
	 */
	useAutomaticResources?: boolean;
	resources?: IResourceCardProps[];
	showTypeLabels?: boolean;
	showArcs?: boolean;
	hideCardExcerpts?: boolean;
	backgroundColor?: 'white' | 'light' | 'themed';
	listingStyle?: 'grid' | 'carousel' | 'list';
	arcPosition?: 'Left' | 'Right';
	allWide?: boolean;
	/**
	 * @control uniqueid
	 * @prefix card-listing
	 */
	uniqueId?: string;
	/**
	 * @noUI
	 */
	allSearchCards?: boolean;
	allTinyCards?: boolean;
	/**
	 * This only applies when listingStyle is set to 'grid'
	 */
	gridDesktopColumns?: 2 | 3 | 4;
	/**
	 * @noUI
	 */
	onFilterTagClick?: (event: any) => any;
	/**
	 * @noUI
	 */
	productCompareLink?: ILink;
	enableProductCompare?: boolean;
	resourceCardsi18n?: IResourceCardsi18n;
}

/**
 * Resource Listing
 * @icon slides
 * @block
 */
const ResourceCardListing: React.FC<IResourceCardListingProps> = ({
	title,
	cta,
	useAutomaticResources,
	resources = [],
	showTypeLabels = false,
	backgroundColor = 'white',
	listingStyle = 'grid',
	headingSize = 'Small',
	headingStyle = 'Columned',
	showArcs = false,
	hideCardExcerpts = false,
	allWide = false,
	arcPosition = 'Right',
	id,
	className,
	classNames = [],
	uniqueId,
	gridDesktopColumns = 3,
	allTinyCards = false,
	excerpt,
	allSearchCards = false,
	enableProductCompare = false,
	productCompareLink,
	onFilterTagClick,
	resourceCardsi18n,
}) => {
	// This component will also be used for Related Posts on the new cards.
	const arcTwo =
		backgroundColor === 'white'
			? arcPosition === 'Right'
				? 'var(--bs-primary)'
				: 'var(--bs-light)'
			: backgroundColor === 'light'
			? 'var(--bs-primary)'
			: 'var(--bs-light)';
	const arcOne =
		backgroundColor === 'white'
			? arcPosition === 'Right'
				? 'var(--bs-light)'
				: 'var(--bs-primary)'
			: backgroundColor === 'light'
			? 'var(--bs-light)'
			: 'var(--bs-light)';
	const arcColors = [arcOne, arcTwo];

	const genUniqueId =
		uniqueId || `card-listing-${Math.random().toString(36).substr(2, 9)}`;

	const defaultBreakpoints = {
		1920: {
			perView: 3.5,
			gap: 32,
		},

		1200: {
			perView: 3.25,
			gap: 32,
		},

		991: {
			perView: 2.5,
			gap: 16,
			after: 100,
		},

		768: {
			perView: 2.25,
			gap: 16,
			after: 100,
		},

		568: {
			perView: 1.5,
			gap: 16,
			after: 100,
		},

		380: {
			perView: 1.2,
			gap: 16,
			after: 100,
		},
	};

	const whitePaperBreakpoints = {
		1920: {
			perView: 2.25,
			gap: 32,
		},
		1440: {
			perView: 2.2,
			gap: 32,
		},

		991: {
			perView: 1.7,
			gap: 16,
			after: 100,
		},

		768: {
			perView: 1.25,
			gap: 16,
			after: 100,
		},

		568: {
			perView: 1.45,
			gap: 16,
			after: 100,
		},
	};

	const carouselOptions = {
		gap: 32,
		type: 'slider',
		rewind: false,
		bound: true,
		perView: allWide ? 2.25 : 3.35, // For huge screens >1920px
		breakpoints: allWide ? whitePaperBreakpoints : defaultBreakpoints,
	};

	//Product Comparison Section
	const p = localStorage.getItem('productToCompare');
	const parsedStorage = JSON.parse(p);
	const [productsToCompare, setProducts] = useState([]);
	const [isIndexPage, setIsIndexPage] = useState(false);
	const [expandedProductBar, setExpandedProductBar] = useState(false);

	const barRef = useRef(null);

	useLayoutEffect(() => {
		// Check if the page is the index page by fetching the index node.
		// This node only exists on the Index Page so it is a good way to catch this.
		// As of right now, this feature only works on GNSS products.
		const indexPageNode = document.querySelector('.gnss-products');
		if (indexPageNode) {
			//Set the boolean so we can use it conditionally later
			setIsIndexPage(true);
			//Enable the locally stored product comparison
			setProducts(parsedStorage || []);
		}
	}, []);

	const productCompare = (product, isStored = false) => {
		if (productsToCompare.includes(product) || isStored) {
			if (isStored) {
				productsToCompare.splice(
					productsToCompare.findIndex(
						(p) => p.post_name === product.post_name,
					),
					1,
				);
			}

			setProducts(productsToCompare.filter((p) => p !== product));
			if (isIndexPage) {
				localStorage.setItem(
					'productToCompare',
					JSON.stringify(
						productsToCompare.filter((p) => p !== product),
					),
				);
			}
		} else {
			if (productsToCompare.length <= 2) {
				setProducts([...productsToCompare, product]);
				if (isIndexPage) {
					localStorage.setItem(
						'productToCompare',
						JSON.stringify([...productsToCompare, product]),
					);
				}
			}
		}
	};

	const submitComparison = () => {
		if (productCompareLink?.href) {
			window.location.href =
				productCompareLink?.href +
				'?products=' +
				productsToCompare.map((product) => product.ID).join('%2C');
		}
	};

	/**
	 * Hide product floating bar on single product page when compare bar is activated
	 */
	const hideFloatingBar = () => {
		const productFloatingBar = document.querySelector(
			'.floating-cta-bar',
		) as HTMLElement;

		if (!productFloatingBar) return;

		productFloatingBar.style.display =
			expandedProductBar && productsToCompare.length > 0
				? 'none'
				: 'block';
	};

	useEffect(() => {
		hideFloatingBar();

		if (expandedProductBar === true) return;

		const productFloatingBar = document.querySelector(
			'.floating-cta-bar > div',
		) as HTMLElement;

		if (!productFloatingBar || !barRef.current) return;

		if (window.innerWidth < 1024) {
			barRef.current.style.bottom =
				productFloatingBar.offsetHeight + 'px';
		} else {
			barRef.current.style.bottom = '0';
		}
	}, [productsToCompare, expandedProductBar]);

	const headerContent = (title || excerpt) && (
		<div className="resource-card-listing-text d-flex flex-column gap-3 mw-641px">
			{title && (
				<h2
					className={cn(
						'mb-0',
						headingSize === 'Small' && 'h3',
						backgroundColor === 'themed' && 'text-white',
					)}
				>
					{title}
				</h2>
			)}
			{excerpt && (
				<p
					className={cn(
						'mb-0',
						headingStyle == 'Card' &&
							listingStyle !== 'carousel' &&
							'text-body-lg',
					)}
				>
					{excerpt}
				</p>
			)}
		</div>
	);
	return (
		<div
			id={id}
			className={cn(
				'resource-card-listing',
				className || [
					'py-8',
					listingStyle !== 'carousel' ? 'px-3' : 'px-0',
					`bg-${backgroundColor}`,
					backgroundColor === 'themed' &&
						'theme-bg-radial-gradient-one',
					'position-relative',
				],
				...classNames,
			)}
		>
			<div className="resource-card-listing__container z-1 position-relative mw-1240px w-100 p-0 mx-auto">
				{(title || cta?.label) && (
					<div
						className={cn(
							'resource-card-listing__head mb-6 px-5 w-100 mx-auto d-inline-flex justify-content-between align-items-start gap-3 flex-wrap',
							headingStyle === 'Centered' &&
								'flex-column text-center align-items-center mx-auto mb-4 mb-md-9',
						)}
					>
						{(headingStyle !== 'Card' ||
							listingStyle === 'carousel') &&
							headerContent}
						{cta?.label && (
							<Button
								size="sm"
								type={
									backgroundColor === 'themed'
										? 'light'
										: 'primary'
								}
								{...cta}
							/>
						)}
					</div>
				)}
				<div
					className={cn(
						'resource-card-listing__cards',
						`resource-card-listing__cards--${listingStyle}`,
						`resource-card-listing__cards--${gridDesktopColumns}-columns`,
						resources?.length === 3 &&
							listingStyle === 'grid' &&
							'resource-card-listing--exactly-three',
					)}
				>
					{listingStyle !== 'carousel' ? (
						<>
							{headingStyle === 'Card' && headerContent}
							{resources?.map((resource, index) => {
								const isStored = parsedStorage?.some(
									//@ts-ignore
									(storedResource) =>
										storedResource.post_name ===
										//@ts-ignore
										resource.post_name,
								);
								return (
									<ResourceCard
										allWide={allWide}
										tinyCard={allTinyCards}
										allSearchList={allSearchCards}
										showTypeLabel={showTypeLabels}
										hideCardExcerpt={hideCardExcerpts}
										enableProductCompare={
											enableProductCompare
										}
										productForCompare={
											productsToCompare.includes(
												resource,
											) || isStored
										}
										productCompareLabel={
											resourceCardsi18n?.cardCompareLabel
										}
										key={index}
										{...resource}
										onProductCompareChange={() => {
											productCompare(resource, isStored);
										}}
										onFilterTagClick={
											onFilterTagClick
												? onFilterTagClick
												: null
										}
									/>
								);
							})}
						</>
					) : (
						<Carousel
							className={cn(
								'resource-card-listing__carousel-glide',
							)}
							id={genUniqueId}
							perView={3.5}
							showBullets={false}
							options={carouselOptions}
						>
							{resources?.map((resource, index) => (
								<CarouselItem key={index}>
									<ResourceCard
										allWide={allWide}
										tinyCard={allTinyCards}
										allSearchList={allSearchCards}
										showTypeLabel={showTypeLabels}
										hideCardExcerpt={hideCardExcerpts}
										key={index}
										{...resource}
									/>
								</CarouselItem>
							))}
						</Carousel>
					)}
				</div>
			</div>
			{showArcs && (
				<AnimatedArcs
					arcsStyle="Hero - Three Lines"
					arcsPosition={arcPosition}
					arcOneColor={arcColors[0]}
					arcOneStrokeWidth={4}
					arcTwoColor={arcColors[1]}
					arcTwoStrokeWidth={3}
				/>
			)}
			{productsToCompare.length > 0 && (
				<div
					className={cn(
						'product-compare-bar position-fixed bottom-0 end-0 z-2 d-flex flex-column w-100',
						/**
						 * This makes sure the bar is at the bottom of the screen when expanded especially when there's a product floating bar.
						 */
						expandedProductBar && '!bottom-0',
					)}
					ref={barRef}
				>
					<button
						onClick={() => {
							setExpandedProductBar(!expandedProductBar);
						}}
						className="product-compare-toggle border-0 align-items-center d-flex gap-2 mx-auto me-md-0 rounded-top bg-primary px-3 py-2 text-white"
					>
						{!expandedProductBar
							? resourceCardsi18n?.viewComparisonLabel ||
							  'View Comparison '
							: resourceCardsi18n?.hideComparisonLabel ||
							  'Hide Comparison '}
						{productsToCompare.length > 0 &&
							`(${productsToCompare.length})`}
						<Icon
							name={
								!expandedProductBar
									? 'chevron-up'
									: 'chevron-down'
							}
						/>
					</button>
					{expandedProductBar && (
						<div className="product-compare-bar-expanded overflow-auto bg-light p-3">
							<div className="product-bar-head mb-3 d-flex flex-column flex-wrap align-items-center gap-2 flex-md-row-reverse">
								<Button
									size="sm"
									type={
										backgroundColor === 'themed'
											? 'light'
											: 'primary'
									}
									icon="chevron-right"
									classNames={['border-0']}
									onClick={() => {
										submitComparison();
									}}
								>
									{resourceCardsi18n?.compareButtonLabel ||
										'Compare Products'}
								</Button>
								<p className="mb-0 text-muted small">
									{resourceCardsi18n?.compareLabel ||
										'Compare up to 3 products'}
								</p>
							</div>
							<div
								className="product-bar-listing d-grid justify-content-center gap-3 flex-md-wrap d-md-flex"
								style={{
									gridTemplateColumns: `repeat(${
										productsToCompare?.length == 1
											? '2'
											: productsToCompare?.length
									}, 1fr)`,
								}}
							>
								{productsToCompare.map((product, index) => (
									<button
										key={index}
										className="product-compare-preview bg-transparent border-0 text-decoration-none text-dark d-inline-flex flex-column align-items-center gap-2 flex-md-row"
										onClick={() => {
											productCompare(product);
										}}
									>
										{product.image && (
											<div className="position-relative">
												<div className="close-icon-indicator position-absolute top-0 end-0 bg-tertiary p-1 rounded-circle text-primary">
													<Icon name="close" />
												</div>
												<Image
													className="img-fluid w-100 rounded"
													{...product.image}
												/>
											</div>
										)}
										{product.heading && (
											<h4 className="mb-0 h6 text-truncate w-100">
												{product.heading}
											</h4>
										)}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
export default ResourceCardListing;
